import apiClient, { apiDomain } from './apiClient';
import { ThemeOptions } from '@mui/material/styles';

export interface FormRecord {
  domainKey: string;
  key: string;
  status: string;
  version: number;
  uuid: string;
  name: string;
  description?: string;
  [field: string]: unknown;
}

export interface LanguageRecord {
  uuid: string;
  languageCode: string;
  domainKey: string;
  content?: unknown;
  [field: string]: unknown;
}

export interface ThemeRecord {
  uuid: string;
  themeName: string;
  domainKey: string;
  content?: unknown;
  [field: string]: unknown;
}

const getResponseContent = (data: unknown): unknown => {
  const content = data && typeof data === 'object' && 'content' in data
    ? (data as { content?: unknown }).content ?? data
    : data;

  return typeof content === 'string' ? JSON.parse(content) : content;
};

const getListFromResponse = <T>(data: unknown, errorMessage: string): T[] => {
  const items = getResponseContent(data);

  if (!Array.isArray(items)) {
    throw new Error(errorMessage);
  }

  return items as T[];
};

export const fetchFormsForDomain = async (): Promise<FormRecord[]> => {
  const response = await apiClient.get(`api/forms/domain/${encodeURIComponent(apiDomain)}/all`);
  return getListFromResponse<FormRecord>(response.data, 'The forms API did not return a list.');
};

export const fetchLanguagesForDomain = async (): Promise<LanguageRecord[]> => {
  const response = await apiClient.get(`api/languages/domain/${encodeURIComponent(apiDomain)}/`);
  return getListFromResponse<LanguageRecord>(response.data, 'The languages API did not return a list.');
};

export const fetchLanguageByUuid = async (uuid: string): Promise<LanguageRecord> => {
  const languages = await fetchLanguagesForDomain();
  const language = languages.find((item) => item.uuid === uuid);

  if (!language) {
    throw new Error('The requested language was not found.');
  }

  return language;
};

export const fetchThemesForDomain = async (): Promise<ThemeRecord[]> => {
  const response = await apiClient.get(`api/themes/domain/${encodeURIComponent(apiDomain)}/`);
  return getListFromResponse<ThemeRecord>(response.data, 'The themes API did not return a list.');
};

export const fetchThemeByUuid = async (uuid: string): Promise<ThemeRecord> => {
  const themes = await fetchThemesForDomain();
  const theme = themes.find((item) => item.uuid === uuid);

  if (!theme) {
    throw new Error('The requested theme was not found.');
  }

  return theme;
};

export const fetchLanguageResource = async (url: string): Promise<{ status: number; data: unknown }> => {
  const response = await apiClient.get(url);

  return {
    status: response.status,
    data: getResponseContent(response.data),
  };
};

export const fetchFormByUuid = async (uuid: string): Promise<FormRecord> => {
  const response = await apiClient.get(`api/forms/${encodeURIComponent(uuid)}`);
  const form = getResponseContent(response.data);

  if (!form || typeof form !== 'object') {
    throw new Error('The form API did not return a form configuration.');
  }

  return form as FormRecord;
};

export const fetchThemeOptions = async (themeName: string): Promise<ThemeOptions> => {
  const response = await apiClient.get(`api/themes/${encodeURIComponent(apiDomain)}/${encodeURIComponent(themeName)}`);
  const themeOptions = getResponseContent(response.data);

  if (!themeOptions || typeof themeOptions !== 'object') {
    throw new Error('The theme API did not return theme options.');
  }

  return themeOptions as ThemeOptions;
};
